import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { IssuedAccount, RsrCard, RsrState } from '@/api/schema';
import { api, ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';

export function RsrActionModal({
  card,
  action,
  onClose,
  onComplete,
}: {
  card: RsrCard;
  action: 'arranged' | 'account';
  onClose: () => void;
  onComplete: (state: RsrState, scheduledOn?: string) => Promise<void>;
}) {
  const { data: user } = useCurrentUser();
  const [date, setDate] = useState(card.rsr_scheduled_on ?? '');
  const [issued, setIssued] = useState<IssuedAccount | null>(null);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const close = () => {
    if (busy) return;
    if (issued) setConfirmClose(true);
    else onClose();
  };
  const run = async () => {
    setProblem(null);
    setBusy(true);
    try {
      if (action === 'arranged') {
        await onComplete('arranged', date);
        onClose();
      } else if (issued || exists) {
        await onComplete('done');
        onClose();
      } else {
        setIssued(await api.post<IssuedAccount>(`/api/v1/ats/candidates/${card.id}/account`));
      }
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ATS_ACCOUNT_EXISTS') setExists(true);
      setProblem(
        error instanceof ApiError
          ? error.message
          : 'The result could not be confirmed. Try again to check before continuing.',
      );
    } finally {
      setBusy(false);
    }
  };
  const ready =
    action === 'arranged'
      ? Boolean(date)
      : Boolean(issued) || exists || can(user, 'ats', 'create');
  return (
    <Modal
      open
      title={
        action === 'arranged'
          ? 'Arrange RSR screening'
          : issued
            ? 'Save the ATS account details'
            : 'Create ATS account to finish screening'
      }
      description={`For ${card.full_name}.`}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={close}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={busy || !ready}
            onClick={() => {
              void run();
            }}
          >
            {action === 'arranged'
              ? 'Save screening date'
              : issued
                ? 'I have copied it — mark Done'
                : exists
                  ? 'Mark screening Done'
                  : 'Create ATS account'}
          </Button>
        </>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {action === 'arranged' ? (
        <Field
          label="Screening date"
          required
          hint="Calendar meetings are scheduled separately."
        >
          {(ids) => (
            <Input
              {...ids}
              type="date"
              required
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
              }}
            />
          )}
        </Field>
      ) : issued ? (
        <>
          <Alert tone="warning">
            This password is shown once. Save both details before continuing.
          </Alert>
          <Field label="Username">
            {(ids) => <Input {...ids} readOnly value={issued.account.username} />}
          </Field>
          <Field label="Password">
            {(ids) => <Input {...ids} readOnly value={issued.password} />}
          </Field>
          <Button
            variant="subtle"
            onClick={() => {
              const clipboard = (navigator as Partial<Navigator>).clipboard;
              if (!clipboard) {
                setProblem('Copy was unavailable. Select and copy the fields above.');
                return;
              }
              void clipboard
                .writeText(`Username: ${issued.account.username}\nPassword: ${issued.password}`)
                .then(
                  () => {
                    setProblem(null);
                  },
                  () => {
                    setProblem('Copy was unavailable. Select and copy the fields above.');
                  },
                );
            }}
          >
            Copy account details
          </Button>
        </>
      ) : (
        <p>
          {can(user, 'ats', 'create')
            ? 'Create the candidate’s ATS account here, save the account details, then mark screening Done. They will appear in the ATS candidates list.'
            : 'A team member with ATS account creation permission needs to create this account before screening can be marked Done.'}
          {exists ? (
            <>
              {' '}
              The account already exists. Continue to Done, or manage its password in{' '}
              <Link to="/ats">ATS candidates</Link>.
            </>
          ) : null}
        </p>
      )}
      {confirmClose ? (
        <Alert tone="warning">
          Have you saved the password? It cannot be shown again.{' '}
          <Button variant="ghost" onClick={onClose}>
            I have saved it — close
          </Button>
        </Alert>
      ) : null}
    </Modal>
  );
}
